import React, { useRef, useState } from 'react'
import { BpButton } from '../../../../shared/components/buttons/bp-button/BpButton'
import { IconButton } from '../../../../shared/components/buttons/icon-button/IconButton'
import { IconTrash } from '../../../../shared/components/icons/IconTrash'
import { PdfFile } from '../../../../shared/ui/illustrations/pdf-file'
import { useTranslation } from 'react-i18next'

export interface IFileDropzoneProps {
  value?: File | null
  extensions?: string[]
  maxSizeMb?: number
  error?: string
  disabled?: boolean
  onChange: (file: File | null) => void
}

const formatSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`

  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

const getExtension = (name: string) => name.split('.').pop()?.toLowerCase() ?? ''

export const FileDropzone = ({
  value,
  extensions = ['pdf', 'docx'],
  maxSizeMb = 50,
  error,
  disabled,
  onChange,
}: IFileDropzoneProps) => {
  const { t } = useTranslation()
  const inputRef = useRef<HTMLInputElement>(null)
  const [isDragOver, setIsDragOver] = useState(false)
  const [validationError, setValidationError] = useState<string>()

  const message = error ?? validationError

  const validate = (file: File) => {
    if (extensions.length && !extensions.includes(getExtension(file.name))) {
      return t('components.fileDropzone.allowedFormats', {
        formats: extensions.map((extension) => extension.toUpperCase()).join(', '),
      })
    }

    if (file.size > maxSizeMb * 1024 * 1024) {
      return t('components.fileDropzone.fileIsTooLarge', {
        maxSizeMb: maxSizeMb,
      })
    }

    return undefined
  }

  const selectFile = (file?: File) => {
    if (!file) return

    const fileError = validate(file)

    setValidationError(fileError)

    if (fileError) return

    onChange(file)
  }

  const browse = () => {
    if (disabled) return

    inputRef.current?.click()
  }

  const remove = () => {
    setValidationError(undefined)
    onChange(null)
  }

  if (value) {
    return (
      <div className={'flex flex-col gap-[4px]'}>
        <div
          className={
            'flex items-center justify-between gap-[16px] rounded-[12px] border border-[#E5E1EA] py-[12px] pl-[16px] pr-[8px]'
          }
        >
          <div className={'flex items-center gap-[16px] overflow-hidden'}>
            <div className={'shrink-0'}>
              <PdfFile />
            </div>
            <div className={'overflow-hidden'}>
              <p className={'truncate text-[15px] leading-[24px] text-focus'}>{value.name}</p>
              <p className={'text-[13px] leading-[18px] text-[#8E7CA7]'}>
                {formatSize(value.size)}
              </p>
            </div>
          </div>
          <IconButton
            className={'border-none shadow-none'}
            disabled={disabled}
            size='lg'
            onClick={remove}
          >
            <IconTrash color='#FF2450' />
          </IconButton>
        </div>
        {!!message && <p className={'text-[13px] leading-[18px] text-[#FF2450]'}>{message}</p>}
      </div>
    )
  }

  return (
    <div className={'flex flex-col gap-[4px]'}>
      <div
        className={`flex flex-col items-center justify-center gap-[6px] rounded-[12px] border border-dashed p-[16px] transition-colors ${
          isDragOver ? 'border-focus bg-focus/[0.05]' : 'border-[#E5E1EA]'
        } ${disabled ? 'pointer-events-none opacity-50' : 'cursor-pointer'}`}
        onDragLeave={(event) => {
          event.preventDefault()
          setIsDragOver(false)
        }}
        onDragOver={(event) => {
          event.preventDefault()
          if (!disabled) setIsDragOver(true)
        }}
        onDrop={(event) => {
          event.preventDefault()
          setIsDragOver(false)

          if (disabled) return

          selectFile(event.dataTransfer.files?.[0])
        }}
        onClick={browse}
      >
        <div className={'flex flex-col items-center justify-center gap-[12px]'}>
          <p className={'font-medium text-focus-500'}>{t('components.fileDropzone.chooseFile')}</p>
          <p className={'whitespace-pre-line text-[13px] text-aqua-300'}>
            {t('components.fileDropzone.formatsAndSize', {
              formats: extensions.map((extension) => extension.toUpperCase()).join(', '),
              maxSizeMb: maxSizeMb,
            })}
          </p>
        </div>
        <BpButton
          className={'w-fit'}
          disabled={disabled}
          size={'xs'}
          theme={'outline'}
          type={'button'}
          onClick={(event) => {
            event.stopPropagation()
            browse()
          }}
        >
          {t('components.fileDropzone.browseFile')}
        </BpButton>
      </div>
      {!!message && <p className={'text-[13px] leading-[18px] text-[#FF2450]'}>{message}</p>}
      <input
        ref={inputRef}
        accept={extensions.map((extension) => `.${extension}`).join(',')}
        className={'hidden'}
        type={'file'}
        onChange={(event) => {
          const file = event.target.files?.[0]

          event.target.value = ''

          selectFile(file)
        }}
      />
    </div>
  )
}
